import React, { useState, useCallback } from 'react';

import { Table, Checkbox, Button } from 'common/lazy';
import { track } from 'utils/analytics';

import { Spacer } from '../Spacer';

import './dialog.less';

const GROUPS = {
  Personalization: {
    title: 'Personalization',
    categories: ['personalization_storage'],
    description: [
      'Remembers your choices, like language or region, to personalize your experience.',
    ],
  },
  Advertising: {
    title: 'Advertising',
    categories: ['ad_storage', 'ad_user_data', 'ad_personalization'],
    description: [
      'Allows us to show you relevant ads and measure their effectiveness, based on your preferences and activity.',
    ],
  },
  Analytics: {
    title: 'Performance & Analytics',
    categories: ['analytics_storage'],
    description: [
      'Helps us understand how you use our site or app so we can improve its performance and your experience.',
    ],
  },
  Essential: {
    title: 'Essential',
    categories: ['functionality_storage', 'security_storage'],
    description: [
      'Necessary for the website or app to work properly and stay secure. These features can’t be turned off.',
    ],
  },
};

const ConsentDialog = () => {
  const [touched, setTouched] = useState(false);
  const [groupPrefs, setGroupPrefs] = useState(() =>
    Object.fromEntries(
      Object.entries(GROUPS).map(([key, { categories }]) => {
        const statuses = categories.map((category) => localStorage[category]);

        if (key === 'Essential') {
          return [key, true];
        }

        return [key, !statuses.includes('denied')];
      })
    )
  );

  const setField = (evt) => {
    const { name, checked } = evt.target;
    setTouched(true);
    setGroupPrefs((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const save = () => {
    const consentOptions = Object.entries(groupPrefs).flatMap(
      ([id, checked]) => {
        return GROUPS[id].categories.map((category) => ({
          option: category,
          status:
            id === 'Essential' ? 'granted' : checked ? 'granted' : 'denied',
        }));
      }
    );

    for (const { option, status } of consentOptions) {
      localStorage.setItem(option, status);
    }

    track('Privacy Prefs Updated');
    window.location.href = '/';
  };

  const renderGroup = useCallback(
    (id) => {
      const { title, description } = GROUPS[id];

      return (
        <Table.Row key={id}>
          <Table.Cell textAlign="center">
            {id === 'Essential' ? (
              <span>N/A</span>
            ) : (
              <Checkbox
                id={id}
                name={id}
                checked={groupPrefs[id]}
                onChange={setField}
                toggle
              />
            )}
          </Table.Cell>
          <Table.Cell className="dialog__category">
            <label htmlFor={id}>{title}</label>
          </Table.Cell>
          <Table.Cell>
            {description.map((d, i) => (
              <p key={i}>{d}</p>
            ))}
          </Table.Cell>
        </Table.Row>
      );
    },
    [groupPrefs]
  );

  const renderGroups = () => Object.keys(GROUPS).map(renderGroup);

  return (
    <div className="dialog">
      <p>
        We use data collected by cookies and JavaScript libraries to improve
        your browsing experience, analyze site traffic, deliver personalized
        advertisements, and increase the overall performance of our site.
      </p>
      <Spacer size="s" />
      <Table basic striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell singleLine>Allow</Table.HeaderCell>
            <Table.HeaderCell singleLine>Category</Table.HeaderCell>
            <Table.HeaderCell singleLine>Purpose</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderGroups()}</Table.Body>
      </Table>
      <Spacer size="s" />
      <div className="dialog__actions">
        <Button onClick={save} disabled={!touched} primary>
          Save
        </Button>
      </div>
    </div>
  );
};

export default ConsentDialog;
